// AdvanceFilter Presentational Component
// This file contains the presentational logic and utility functions for the AdvanceFilter UI component.
// It provides a filter selection interface with search, select/unselect, and tag removal features.

import { useState, ChangeEventHandler, MouseEventHandler } from "react";
import {
  Input,
  Button,
  SelectionList,
  RemovableTag,
  SelectionListStructuredValue,
} from "@/vendor/Forge/dist";
import SearchIcon from "@/components/Icons/search";
import { useOrdersContext } from "@/contexts/orders-context";
import { Filter, Filters } from "@/types/order-table";
import {
  Wrapper,
  CTA,
  Content,
  LeftPanel,
  RightPanel,
  Search,
  Summary,
  Tags,
} from "./components.stitches";

/**
 * Sets all values in a string array to a given boolean value, returning an object keyed by the string values.
 * @param originalList - Array of string keys
 * @param value - Boolean value to assign to each key
 * @returns Object with each key set to the given value
 */
export const setAllValuesTo = (
  originalList: string[],
  value: boolean,
): SelectionListStructuredValue => {
  return originalList.reduce((acc, curr) => {
    return { ...acc, [curr]: value };
  }, {});
};

// Maps filter types to their corresponding filter keys in the Filters object
const filtersMap: {
  [key: string]: Filter;
} = {
  insurance: "insurances",
  provider: "providerNames",
  department: "departments",
  authStatus: "authStatus",
  orderName: "orderTypes",
  patientName: "patientNames",
  scheduledStatus: "scheduledStatus",
  facility: "facility",
};

/**
 * Initializes the master selection list for the filter UI, using active filters if present.
 * @param originalList - All possible filter options
 * @param type - Filter type
 * @param activeFilters - Current active filters
 * @returns SelectionListStructuredValue with selected state for each option
 */
export const setInitialMasterList = (
  originalList: string[],
  type: Filter,
  activeFilters: Filters,
) => {
  const activeFiltersAsSet = new Set<string>([
    ...(activeFilters?.[filtersMap[type]] ?? []),
  ]);
  if (!activeFiltersAsSet.size) return setAllValuesTo(originalList, true);

  const convertedActiveFilters = Array.from(activeFiltersAsSet);

  return [...originalList, ...convertedActiveFilters].reduce(
    (acc, curr) => ({ ...acc, [curr]: activeFiltersAsSet.has(curr) }),
    {},
  );
};

/**
 * Returns a list of selected keys from the master selection object.
 * @param masterList - Object with boolean values for each key
 * @returns Array of selected keys
 */
export const getSelectedList = (masterList: SelectionListStructuredValue) => {
  return Object.keys(masterList).filter((key) => masterList[key]);
};

/**
 * Converts a master selection object to a structured value for the SelectionList component.
 * @param masterList - Object with boolean values for each key
 * @returns Object with only selected keys set to true
 */
export const convertListToSelectionListStructuredValue = (
  masterList: SelectionListStructuredValue,
) => {
  return getSelectedList(masterList).reduce(
    (acc, curr) => ({ ...acc, [curr]: true }),
    {},
  );
};

/**
 * Props for the AdvanceFilter presentational component
 */
interface Props {
  type: Filter;
  originalList: string[];
  closeModal?: () => void;
}

/**
 * AdvanceFilter Presentational Component
 * Provides a UI for filtering a list of items with search, select/unselect, and tag removal features.
 *
 * @param type - The filter type (e.g., 'orderName', 'provider')
 * @param originalList - The list of all possible filter options
 * @param closeModal - Optional callback to close the modal
 */
const AdvanceFilter = ({ type, originalList, closeModal }: Props) => {
  // Get active filters and update function from context
  const { filters: activeFilters, updateFilters } = useOrdersContext();
  // State for the search input value
  const [searchKey, setSearchKey] = useState("");
  // State for the master selection list (all options and their selected state)
  const [masterList, setMasterList] = useState<SelectionListStructuredValue>(
    setInitialMasterList(originalList, type, activeFilters),
  );
  // State for the filtered list based on search
  const [filteredList, setFilteredList] = useState<string[]>(originalList);
  // List of currently selected items
  const selectedItems = getSelectedList(masterList);

  /**
   * Handles clicking or keyboard selection of an item in the list.
   * Toggles the selected state for the given key.
   */
  const handleItemClick = (
    _:
      | React.MouseEvent<HTMLUListElement, MouseEvent>
      | React.KeyboardEvent<HTMLUListElement>,
    key: string | null,
  ) => {
    if (key === null) return;
    setMasterList({
      ...masterList,
      [key]: !masterList[key],
    });
  };

  /**
   * Clears all selections and resets the search/filter state.
   */
  const handleClearAllClick = () => {
    setMasterList(setAllValuesTo(originalList, false));
    setFilteredList(originalList);
    setSearchKey("");
  };

  /**
   * Handles search input changes, filtering the list accordingly.
   */
  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    const key = e.target.value;
    setSearchKey(key);
    setFilteredList(
      originalList.filter((l) =>
        l.toLowerCase().includes(key.toLocaleLowerCase()),
      ),
    );
  };

  /**
   * Handles removing a selected tag (deselects the item).
   */
  const handleRemoveTag = (key: string) => {
    setMasterList({
      ...masterList,
      [key]: !masterList[key],
    });
  };

  /**
   * Applies the selected filters and closes the modal if provided.
   */
  const handleFilterResultsClick: MouseEventHandler<HTMLButtonElement> = () => {
    updateFilters(filtersMap[type], selectedItems);
    closeModal?.();
  };

  return (
    <Wrapper data-testid="wrapper-filters">
      <Content>
        <LeftPanel>
          <Search>
            <Input
              placeholder="Search"
              icon={SearchIcon}
              onChange={handleSearch}
              value={searchKey}
            />
            {/* Button to unselect all items */}
            <Button variant="tertiary" onClick={handleClearAllClick}>
              Unselect All
            </Button>
          </Search>
          {/* List of filter options with selection state */}
          <SelectionList
            options={filteredList}
            onClickItem={handleItemClick}
            value={convertListToSelectionListStructuredValue(masterList)}
          />
        </LeftPanel>
        <RightPanel>
          <Summary>
            <span>Selected({selectedItems.length})</span>
            {/* Button to clear all selections */}
            <Button variant="tertiary" onClick={handleClearAllClick}>
              Clear All
            </Button>
          </Summary>
          {/* Tags for each selected item, removable by clicking the tag */}
          <Tags>
            {selectedItems.map((selectedItem) => (
              <RemovableTag
                key={selectedItem}
                text={selectedItem}
                onRemove={() => handleRemoveTag(selectedItem)}
              />
            ))}
          </Tags>
        </RightPanel>
      </Content>
      {/* Call-to-action button to apply filters */}
      <CTA>
        <Button onClick={handleFilterResultsClick}>Filter Results</Button>
      </CTA>
    </Wrapper>
  );
};

export default AdvanceFilter;
